import React from 'react'
import { useSelector } from 'react-redux'
import { getVideo } from 'Utils/videoUrls'
import {
  useGetVideoByIdQuery,
  useGetPanoramaByIdQuery,
  useGetPhotoByIdQuery,
} from 'Services/institution'
import { Box, Tooltip, Skeleton } from '@mui/material'
import { useMediaDetails } from 'Hooks/useMediaDetails'
import useTooltipEnabled from 'Hooks/useTooltipEnabled'
import { AccessibleImage } from 'Components/accessibleImage'
import DOMPurify from 'dompurify'

interface MainMediaProps {
  id: number
  type: string
  info?: boolean
  pointMediaId?: number
  stopId?: number
  stopTitle: string
}

export const MainMedia = ({
  id,
  type,
  info,
  pointMediaId,
  stopId,
  stopTitle,
}: MainMediaProps) => {
  const tooltipEnabled = useTooltipEnabled()
  const { locId, instId } = useSelector((state: any) => state.location)
  console.log("tyope", type)

  const { data: videoData, isLoading: videoLoading } = useGetVideoByIdQuery(
    { instId, locId, videoId: id },
    { skip: type !== 'video' }
  )
  const video = videoData?.resources?.video
  const approvedVideo = video?.status === '1' && video?.approved === '1'

  const {
    data: photoData,
    isLoading: photoLoading,
    isSuccess: photoSuccess,
  } = useGetPhotoByIdQuery(
    { instId, locId, photoId: id },
    { skip: type !== 'photo' }
  )
  const photo = photoData?.resources?.photo
  const approvedPhoto = photo?.status === '1'

  const {
    data: panoramaData,
    isLoading: panoramaLoading,
    isSuccess: panoramaSuccess,
  } = useGetPanoramaByIdQuery(
    { instId, locId, panoramaId: id },
    { skip: type !== 'panorama' }
  )
  const panorama = panoramaData?.resources?.panorama
  const approvedPanorama = panorama?.status === '1'

  const { photoAssetUrl: pointAssetUrl } = useMediaDetails(stopId)

  const youVisitAssetUrl =
    photoData?.resources?.metadata?.path?.youvisit?.slice(
      0,
      photoData?.resources?.metadata?.path?.youvisit.indexOf('/media')
    ) ||
    panoramaData?.resources?.metadata?.path?.slice(
      0,
      panoramaData?.resources?.metadata?.path?.indexOf('/media')
    )

  if (videoLoading || photoLoading || panoramaLoading) {
    return <Skeleton variant='rectangular' width={'100%'} height={450} />
  }

  if (type === 'video' && video) {
    const videoAccessibilityDescription = video?.accessibility_description
    const videoDescriptionPart =
      videoAccessibilityDescription || video?.description || ''
    const videoAccessibilityText = [video?.title, videoDescriptionPart]
      .filter((part) => part != null && String(part).trim() !== '')
      .join('. ')
    return (
      <div>
        {video?.title && (
          <p style={{ margin: 0, fontWeight: 700 }}>{video?.title}</p>
        )}
        <Tooltip title={videoAccessibilityText || video?.title} placement='top' arrow disableHoverListener={!tooltipEnabled} disableFocusListener={!tooltipEnabled} disableTouchListener={!tooltipEnabled}>
          <div style={{ display: 'flex' }}>{getVideo(video)}</div>
        </Tooltip>
        {video?.description && (
          <p
            style={{ margin: 0 }}
            aria-label={videoAccessibilityDescription}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(video?.description, {
                ADD_ATTR: ['target'],
              }),
            }}
          />
        )}
        <br />
        <br />
      </div>
    )
  }

  const isPointMedia = !type && pointMediaId && pointAssetUrl

  if (
    (type === 'photo' && photoSuccess) ||
    (type === 'panorama' && panoramaSuccess) ||
    isPointMedia
  ) {
    const mediaType = type === 'panorama' ? 'panoramas' : 'photos'
    const mediaId = type === 'panorama' ? panorama?.id : photo?.photoid
    const mediaTitle = type === 'panorama' ? panorama?.title : photo?.title
    const mediaNetwork =
      type === 'photo' || type === 'video'
        ? photo?.network || video?.network
        : null
    const mediaDescription =
      type === 'panorama' ? panorama?.description : photo?.description
    const mediaAccessibilityDescription =
      type === 'panorama'
        ? panorama?.accessibility_description
        : photo?.accessibility_description

    // media without type uses point media url structure
    const mediaUrl = type
      ? `${youVisitAssetUrl}/media/${locId}/${mediaType}/${mediaId}/1024.jpg`
      : `${pointAssetUrl}/media/${locId}/tour/${pointMediaId}/1024_front.jpg`

    const igUrl = mediaNetwork === 'instagram' ? photo?.url : null

    const renderMedia = (mediaNetwork: string) => {
      const instagramWidth = '800px'
      const instagramHeight = '650px'
      if (mediaNetwork !== 'instagram') {
        const descriptionPart =
          mediaAccessibilityDescription || mediaDescription || ''
        const accessibilityText = [mediaTitle, descriptionPart]
          .filter((part) => part != null && String(part).trim() !== '')
          .join('. ')
        const imageAlt =
          accessibilityText ||
          `This is the ${stopTitle} tour stop image`
        return (
          <Box
            component='div'
            sx={{
              width: '100%',
              height: 'auto',
              background: '#f2f3f4',
              textAlign: 'center',
            }}
          >

            {mediaUrl && (mediaId || pointMediaId) && (
              <AccessibleImage
                src={mediaUrl}
                alt={imageAlt}
                tooltipTitle={accessibilityText || imageAlt}
                sx={{ maxHeight: '450px', maxWidth: '100%' }}
              />
            )}
          </Box>
        )
      } else {
        return (
          <iframe
            aria-hidden='true'
            src={`https://www.instagram.com/p/${igUrl}/embed`}
            title='Instagram video player'
            frameBorder='0'
            scrolling='true'
            width={instagramWidth}
            height={instagramHeight}
            style={{
              maxWidth: instagramWidth,
              maxHeight: instagramHeight,
              width: '100%',
            }}
            data-testid={`instagram-video-${photo.photoid}`}
          />
        )
      }
    }

    if (approvedVideo || approvedPanorama || approvedPhoto || isPointMedia)
      return (
        <>
          {info && <h4>{mediaTitle}</h4>}
          {renderMedia(mediaNetwork)}
          {info && (
            <p
              style={{ margin: 0 }}
              aria-label={mediaAccessibilityDescription}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  mediaDescription || mediaAccessibilityDescription,
                  { ADD_ATTR: ['target'] }
                ),
              }}
            />
          )}
        </>
      )
  }

  return null
}
